import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { hook, withValue } from '@g4/automation-hooks';

import { hookAttribute } from '../../../utils/automationHookUtils';
import { useTranslations } from '../../../graphql/components/TranslationsProvider';
import {
  VerticalSpacing,
  Text,
  Media,
  Box,
} from '../../../components/Boneyard';
import Markdown from '../../../components/Markdown';

import PLANE_FLYING_BACKGROUND from '../../../images/plane-flying-bg.svg';
import PLUS_ICON from '../../../images/ic-plus.svg';
import MINUS_ICON from '../../../images/ic-minus.svg';

import { FLIGHTS_ROUND_TRIP_DISCOUNT } from '../../../../automationHooks.json';

const propTypes = {
  amount: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  lateRoundTrip: PropTypes.bool,
};

const Styled = {
  Button: styled.button`
    position: relative;
    width: 100%;
    background-color: ${({ theme: { colors } }) =>
      colors.flights.roundTripBanner.callToActionBg};
  `,
  Description: styled(Box)`
    height: 100%;
    flex-grow: 1;
    text-align: left;
    color: ${({ theme: { colors } }) =>
      colors.flights.roundTripBanner.descriptionText};
    background-color: ${({ theme: { colors } }) =>
      colors.flights.roundTripBanner.descriptionBg};
    padding: ${props => props.theme.spacing(3)}
      ${props => props.theme.spacing(4)};

    ${props => props.theme.query.md} {
      flex-basis: ${props => props.theme.flights.list.priceWidth.desktop};

      padding-left: ${props => props.theme.spacing(8)};
      padding-right: ${props => props.theme.spacing(8)};

      background-image: url(${PLANE_FLYING_BACKGROUND});
      background-repeat: no-repeat;
      background-position: right 30px top 0;
    }
  `,
  CallToAction: styled(Box)`
    font-size: ${props => props.theme.toRem(12)};
    font-weight: 700;
    text-transform: uppercase;
    color: ${props => props.theme.colors.common.text};
    padding: 0 ${props => props.theme.toRem(1)};
    flex-shrink: 0;
    flex-basis: ${props => props.theme.flights.list.priceWidth.mobile};

    ${props => props.theme.query.lg} {
      font-size: ${props => props.theme.toRem(16)};
      flex-basis: ${props => props.theme.flights.list.priceWidth.desktop};
      padding: 0 ${props => props.theme.spacing(5)};
    }
  `,
};

const RoundTripDiscountBanner = ({ amount, onClick, lateRoundTrip }) => {
  const {
    flightsPage: {
      roundTripBannerDescriptionTitle,
      roundTripBannerDescriptionText,
      roundTripBannerCallToAction,
      roundTripCancelButtonLabel,
    },
  } = useTranslations();

  return (
    <Media>
      {mq => {
        const isMobile = mq.lte('sm');

        return (
          <Styled.Button
            type="button"
            {...hook(FLIGHTS_ROUND_TRIP_DISCOUNT.hook)}
            onClick={onClick}
          >
            <Box d="flex" alignItems="center" justifyContent="space-between">
              <Styled.Description>
                <Text
                  size={isMobile ? 12 : 26}
                  weight={700}
                  {...hook(
                    FLIGHTS_ROUND_TRIP_DISCOUNT.hook,
                    'description-line-one'
                  )}
                >
                  {roundTripBannerDescriptionTitle}
                </Text>
                <VerticalSpacing />
                <Text
                  size={isMobile ? 12 : 16}
                  lh={1.5}
                  automationHook={withValue(
                    FLIGHTS_ROUND_TRIP_DISCOUNT.hook,
                    'description-line-two'
                  )}
                >
                  <Markdown
                    source={roundTripBannerDescriptionText}
                    data={{
                      amount: `<strong ${hookAttribute(
                        FLIGHTS_ROUND_TRIP_DISCOUNT.hook,
                        'description-amount'
                      )}>$${amount}</strong>`,
                    }}
                  />
                </Text>
              </Styled.Description>
              <Styled.CallToAction>
                <Box
                  d="flex"
                  direction={mq.lte('md') ? 'column' : 'row'}
                  alignItems="center"
                  justifyContent={mq.lte('md') ? 'center' : 'space-around'}
                  automationHook={withValue(
                    FLIGHTS_ROUND_TRIP_DISCOUNT.hook,
                    lateRoundTrip
                      ? 'hide-return-flights-button'
                      : 'view-return-flights-button'
                  )}
                >
                  <Markdown
                    source={
                      lateRoundTrip
                        ? roundTripCancelButtonLabel
                        : roundTripBannerCallToAction
                    }
                  />
                  <img
                    src={lateRoundTrip ? MINUS_ICON : PLUS_ICON}
                    alt="Plus Icon"
                    width={mq.lte('md') ? '24px' : '36px'}
                  />
                </Box>
              </Styled.CallToAction>
            </Box>
          </Styled.Button>
        );
      }}
    </Media>
  );
};
RoundTripDiscountBanner.propTypes = propTypes;

export default RoundTripDiscountBanner;
